import { useEffect, useState } from "react";
import { SCHEDULED_VALUE_TEXT_PRESETS } from "@/components/buy-log/ScheduledValueCell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { CostCode } from "@/hooks/use-budgets";

interface BuyLogAddLineItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  costCodes: CostCode[];
  hasGeneralContractor: boolean;
  onSubmit: (data: {
    costCodeId: string;
    description: string;
    csiDivision: string | null;
    originalBudgetedAmt: string;
    isAllowance: boolean;
    scheduledValueText: string | null;
  }) => void;
  isPending: boolean;
}

/**
 * Dialog for creating a new Buy Log line item at the budgeting stage (spec
 * §6 Step 1). Collects only the cost code, description, and Scheduled Value
 * — award information is entered separately once a subcontractor is
 * selected.
 */
export function BuyLogAddLineItemDialog({
  open,
  onOpenChange,
  costCodes,
  hasGeneralContractor,
  onSubmit,
  isPending,
}: BuyLogAddLineItemDialogProps) {
  const [costCodeId, setCostCodeId] = useState("");
  const [description, setDescription] = useState("");
  const [scheduledValue, setScheduledValue] = useState("");
  const [svMode, setSvMode] = useState<"numeric" | (typeof SCHEDULED_VALUE_TEXT_PRESETS)[number]>(
    "numeric",
  );
  const [isAllowance, setIsAllowance] = useState(false);

  useEffect(() => {
    if (!open) {
      setCostCodeId("");
      setDescription("");
      setScheduledValue("");
      setSvMode("numeric");
      setIsAllowance(false);
    }
  }, [open]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!costCodeId || !description) return;
    const chosen = costCodes.find((c) => c.id === costCodeId);
    const usingText = svMode !== "numeric";
    onSubmit({
      costCodeId,
      description,
      csiDivision: chosen?.csiDivision ?? null,
      originalBudgetedAmt: usingText ? "0" : scheduledValue === "" ? "0" : scheduledValue,
      isAllowance,
      scheduledValueText: usingText ? svMode : null,
    });
  }

  const selectedCostCode = costCodes.find((c) => c.id === costCodeId);
  const isOwnerPayAppCode = selectedCostCode?.ownerPayApp === true;
  const blockedForMissingGc = isOwnerPayAppCode && !hasGeneralContractor;
  const canSubmit =
    costCodeId !== "" && description.trim().length > 0 && !isPending && !blockedForMissingGc;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Buy Log Line Item</DialogTitle>
          <DialogDescription>
            Enter the scope of work and its estimated budget. You can assign a subcontractor and
            enter the award value after bids are received.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cost-code">Cost Code</Label>
            <Select value={costCodeId} onValueChange={setCostCodeId}>
              <SelectTrigger id="cost-code">
                <SelectValue placeholder="Select a cost code" />
              </SelectTrigger>
              <SelectContent>
                {costCodes.map((code) => (
                  <SelectItem key={code.id} value={code.id}>
                    {code.code} — {code.description ?? ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {blockedForMissingGc && (
              <p className="text-xs text-red-600">
                This cost code rolls into the Owner Pay App and requires a General Contractor. Set
                one on the project first.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="e.g. Structural steel — erection only"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sv-mode">Scheduled Value</Label>
            <Select value={svMode} onValueChange={(v) => setSvMode(v as typeof svMode)}>
              <SelectTrigger id="sv-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="numeric">Enter amount</SelectItem>
                {SCHEDULED_VALUE_TEXT_PRESETS.map((preset) => (
                  <SelectItem key={preset} value={preset}>
                    {preset}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {svMode === "numeric" ? (
              <Input
                id="sv"
                type="number"
                step="0.01"
                min="0"
                value={scheduledValue}
                onChange={(e) => setScheduledValue(e.target.value)}
                placeholder="0.00"
              />
            ) : (
              <p className="text-xs text-gray-500">
                This line will carry $0 but preserve the <strong>{svMode}</strong> intent on the Buy
                Log and G703.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="is-allowance"
              checked={isAllowance}
              onCheckedChange={(next) => setIsAllowance(next === true)}
            />
            <Label htmlFor="is-allowance" className="text-sm font-normal">
              Allowance line (flows through to G703)
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? "Adding..." : "Add Line Item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
